import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, Info } from 'lucide-react';

/**
 * Visualizador 24h de una plantilla de turnos:
 *   - Cada banda se pinta como un bloque de color (Mañana / Tarde / Noche).
 *   - Los solapes entre bandas se marcan en amarillo con tooltip (indica
 *     qué banda "gana" según la nueva regla del backend: banda más
 *     reciente = la que empezó más cerca del punto medio del solape).
 *   - Los huecos sin cobertura se marcan en gris.
 *
 * Se actualiza automáticamente cada vez que cambian las bandas (llama a
 * POST /api/shift-templates/analyze con debounce ligero).
 */
const BAND_COLORS = {
  'Mañana':  { fill: 'rgba(96,165,250,0.85)',  border: '#60a5fa' },   // azul
  'Tarde':   { fill: 'rgba(251,146,60,0.85)',  border: '#fb923c' },   // naranja suave
  'Noche':   { fill: 'rgba(139,92,246,0.85)',  border: '#8b5cf6' },   // púrpura
  'Partido': { fill: 'rgba(74,222,128,0.85)',  border: '#4ade80' },   // verde
};

function bandColor(name) {
  return BAND_COLORS[name] || { fill: 'rgba(148,163,184,0.85)', border: '#94a3b8' };
}

// Convierte una banda a intervalos [startMin, endMin] (uno o dos si wrap)
function bandIntervals(b) {
  const s = (b.start_hour ?? 0) * 60 + (b.start_minute ?? 0);
  const e = (b.end_hour   ?? 0) * 60 + (b.end_minute   ?? 0);
  if (e > s) return [[s, e]];
  if (e === s) return [];
  return [[s, 1440], [0, e]];
}

function fmt(mins) {
  const h = Math.floor(mins / 60), m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function ShiftTemplateTimeline({ shifts }) {
  const { apiFetch } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  // Debounce simple del análisis
  useEffect(() => {
    if (!shifts || shifts.length === 0) { setAnalysis(null); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiFetch('/api/shift-templates/analyze', {
          method: 'POST',
          body: JSON.stringify({
            shifts: shifts.map(s => ({
              name: s.name,
              start_hour:   Number(s.start_hour)   || 0,
              start_minute: Number(s.start_minute) || 0,
              end_hour:     Number(s.end_hour)     || 0,
              end_minute:   Number(s.end_minute)   || 0,
              weekdays:     s.weekdays && s.weekdays.length < 7 ? s.weekdays : null,
              puesto_keywords: (s.puesto_keywords && s.puesto_keywords.length) ? s.puesto_keywords : null,
            })),
          }),
        });
        if (res.ok) setAnalysis(await res.json());
      } catch (_) { /* silencioso */ }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [shifts, apiFetch]);

  if (!shifts || shifts.length === 0) return null;

  const width = 100;   // porcentaje base
  const overlaps = analysis?.overlaps || [];
  const gaps     = analysis?.gaps || [];
  const puestoOverrides = analysis?.puesto_overrides || [];
  const genericShifts = shifts.filter(s => !(s.puesto_keywords && s.puesto_keywords.length));
  const puestoShifts  = shifts.filter(s => s.puesto_keywords && s.puesto_keywords.length);

  return (
    <div data-testid="shift-timeline" style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 14, marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#94a3b8', fontSize: 12, fontWeight: 500 }}>
          <Info size={14} />
          Cobertura 24h {loading && <span style={{ color: '#475569' }}>· analizando…</span>}
        </div>
        {analysis && (
          <span style={{ color: '#64748b', fontSize: 11 }}>
            {analysis.coverage_pct}% cubierto · {overlaps.length} solape(s) · {gaps.length} hueco(s)
          </span>
        )}
      </div>

      {/* Timeline visual 24h */}
      <div style={{ position: 'relative', height: 36, background: '#020617', borderRadius: 6, overflow: 'hidden' }} data-testid="shift-timeline-bar">
        {/* Marcas horarias */}
        {[0, 3, 6, 9, 12, 15, 18, 21, 24].map(h => (
          <div key={h} style={{
            position: 'absolute', left: `${(h / 24) * width}%`, top: 0, bottom: 0,
            borderLeft: h === 0 || h === 24 ? 'none' : '1px dashed rgba(148,163,184,0.15)',
            width: 1, pointerEvents: 'none',
          }} />
        ))}
        {/* Bandas genéricas (cuentan para cobertura/solapes del centro) */}
        {genericShifts.flatMap((b, i) => bandIntervals(b).map(([s, e], j) => {
          const c = bandColor(b.name);
          const left = (s / 1440) * width;
          const w    = ((e - s) / 1440) * width;
          return (
            <div
              key={`${i}-${j}`}
              title={`${b.name}: ${fmt(s)}–${fmt(e)}`}
              style={{
                position: 'absolute', top: 4, bottom: 4,
                left: `${left}%`, width: `${w}%`,
                background: c.fill, borderLeft: `2px solid ${c.border}`,
                borderRadius: 3,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', fontSize: 10, color: '#020617', fontWeight: 700,
              }}
            >
              {w > 4 && b.name}
            </div>
          );
        }))}
        {/* Overlays de solape (encima de las bandas) */}
        {overlaps.map((ov, i) => {
          const left = (ov.start_min / 1440) * width;
          const w    = ((ov.end_min - ov.start_min) / 1440) * width;
          return (
            <div
              key={`ov-${i}`}
              data-testid={`shift-timeline-overlap-${i}`}
              title={`Solape ${ov.start}–${ov.end} entre ${ov.bands.join(' y ')}. Ganará: ${ov.winner}`}
              style={{
                position: 'absolute', top: 0, bottom: 0,
                left: `${left}%`, width: `${w}%`,
                background: 'repeating-linear-gradient(45deg, rgba(251,191,36,0.55) 0 4px, rgba(251,191,36,0.15) 4px 8px)',
                border: '1px solid rgba(251,191,36,0.7)',
                cursor: 'help',
              }}
            />
          );
        })}
        {/* Overlays de hueco */}
        {gaps.map((g, i) => {
          const left = (g.start_min / 1440) * width;
          const w    = ((g.end_min - g.start_min) / 1440) * width;
          return (
            <div
              key={`gap-${i}`}
              data-testid={`shift-timeline-gap-${i}`}
              title={`Hueco sin cubrir ${g.start}–${g.end}. Los fichajes ahí caerán al criterio global (por hora).`}
              style={{
                position: 'absolute', top: 0, bottom: 0,
                left: `${left}%`, width: `${w}%`,
                background: 'repeating-linear-gradient(90deg, rgba(148,163,184,0.4) 0 3px, rgba(148,163,184,0.1) 3px 6px)',
                border: '1px dashed rgba(148,163,184,0.5)',
                cursor: 'help',
              }}
            />
          );
        })}
      </div>

      {/* Etiquetas horarias */}
      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', fontSize: 9, fontFamily: 'IBM Plex Mono', marginTop: 4 }}>
        {[0, 3, 6, 9, 12, 15, 18, 21, 24].map(h => (
          <span key={h}>{String(h).padStart(2, '0')}h</span>
        ))}
      </div>

      {/* Franja secundaria: bandas restringidas a un puesto (no cuentan en la cobertura del centro) */}
      {puestoShifts.length > 0 && (
        <div style={{ marginTop: 10 }} data-testid="shift-timeline-puesto-lane">
          <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>Overrides por puesto (solo esos puestos, no cuentan en la cobertura del centro)</div>
          <div style={{ position: 'relative', height: 22, background: '#020617', borderRadius: 6, overflow: 'hidden', opacity: 0.9 }}>
            {puestoShifts.flatMap((b, i) => bandIntervals(b).map(([s, e], j) => {
              const c = bandColor(b.name);
              const left = (s / 1440) * width;
              const w    = ((e - s) / 1440) * width;
              return (
                <div
                  key={`pk-${i}-${j}`}
                  title={`${b.name} (${(b.puesto_keywords || []).join(', ')}): ${fmt(s)}–${fmt(e)}`}
                  style={{
                    position: 'absolute', top: 2, bottom: 2,
                    left: `${left}%`, width: `${w}%`,
                    background: 'transparent', border: `1.5px dashed ${c.border}`,
                    borderRadius: 3,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', fontSize: 9, color: c.border, fontWeight: 700,
                  }}
                >
                  {w > 6 && b.name}
                </div>
              );
            }))}
          </div>
          <ul style={{ margin: '6px 0 0 16px', padding: 0, fontSize: 11, color: '#94a3b8' }}>
            {puestoOverrides.map((po, i) => (
              <li key={i}>
                {po.start}–{po.end}: <strong style={{ color: '#fb923c' }}>{po.name}</strong> — solo para puesto(s): {(po.puesto_keywords || []).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Avisos textuales */}
      {overlaps.length > 0 && (
        <div data-testid="shift-timeline-overlap-list" style={{
          display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 10,
          background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.35)',
          borderRadius: 6, padding: '8px 10px',
        }}>
          <AlertTriangle size={14} style={{ color: '#fbbf24', flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 11, color: '#fbbf24', lineHeight: 1.5 }}>
            <strong>Solapes detectados</strong> — no dan error, pero conviene saber qué gana:
            <ul style={{ margin: '4px 0 0 14px', padding: 0 }}>
              {overlaps.map((ov, i) => (
                <li key={i}>
                  {ov.start}–{ov.end}: {ov.bands.join(' y ')} → clasifica como <strong>{ov.winner}</strong>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {gaps.length > 0 && (
        <div data-testid="shift-timeline-gap-list" style={{
          display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 8,
          background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.3)',
          borderRadius: 6, padding: '8px 10px',
        }}>
          <Info size={14} style={{ color: '#94a3b8', flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 11, color: '#cbd5e1', lineHeight: 1.5 }}>
            <strong>Huecos sin cubrir</strong> — los fichajes ahí caerán al criterio global (por hora):
            <ul style={{ margin: '4px 0 0 14px', padding: 0 }}>
              {gaps.map((g, i) => <li key={i}>{g.start}–{g.end}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
